import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { getCandidato, getCandidatoPdfUrl, getCandidatoCartaPdfUrl, deleteCandidato, updateCandidato, reescanearCandidato, actualizarCandidatoCV, getListas, createLista, addCandidatoToLista, removeCandidatoFromLista, setCarpetaEnLista, getCandidatosDeLista, isAuthenticated, getToken, INTEREST_WEIGHTS_UPDATED_EVENT, INTEREST_WEIGHTS_STORAGE_KEY } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useUnopenedCandidatos } from "@/contexts/UnopenedCandidatosContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ArrowLeft, ArrowRight, Mail, Phone, Briefcase, Clock, Award, Trash2, Loader2, FileText, ExternalLink, MapPin, Car, Languages, Link, Globe, GraduationCap, BookOpen, Trophy, StickyNote, DollarSign, RefreshCw, PhoneCall, Upload, User, Archive, ArchiveRestore, ListChecks, Plus, Check, Heart, ChevronDown, ChevronRight, XCircle, MoreVertical, Pencil } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { AppMainHeader } from "@/components/AppMainHeader";

function SocialIcon({ url, tipo, className = "w-4 h-4" }) {
  const u = (url || "").toLowerCase();
  const t = (tipo || "").toLowerCase();
  const match = (domains) => domains.some((d) => u.includes(d) || t.includes(d.split(".")[0]));

  if (match(["instagram.com", "instagram"])) return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
    </svg>
  );
  if (match(["linkedin.com", "linkedin"])) return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
    </svg>
  );
  if (match(["youtube.com", "youtu.be", "youtube"])) return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.96-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="currentColor" stroke="none"/>
    </svg>
  );
  if (match(["twitter.com", "x.com", "twitter", "/x"])) return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );
  if (match(["tiktok.com", "tiktok"])) return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
    </svg>
  );
  if (match(["facebook.com", "fb.com", "facebook"])) return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
    </svg>
  );
  if (match(["pinterest.com", "pinterest"])) return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
    </svg>
  );
  if (match(["behance.net", "behance"])) return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M22 7h-7V5h7v2zm1.726 10c-.442 1.297-2.029 3-5.101 3-3.074 0-5.564-1.729-5.564-5.675 0-3.91 2.325-5.92 5.466-5.92 3.082 0 4.964 1.782 5.375 4.426.078.506.109 1.188.095 2.14H15.97c.13 1.202.836 1.287 1.995 1.287.901 0 1.519-.435 1.76-1.258zm-7.73-3.7h3.619c-.094-1.106-.72-1.603-1.765-1.603-1.027 0-1.69.519-1.854 1.603zM3.654 5.328c1.765 0 3.12.6 3.12 2.4 0 .96-.496 1.638-1.27 2.026.982.292 1.66 1.064 1.66 2.292 0 2.047-1.452 2.954-3.573 2.954H0V5.328h3.654zm-.262 3.887c.803 0 1.316-.376 1.316-1.164 0-.742-.478-1.098-1.244-1.098H2.4v2.262h.992zm.101 4.127c.895 0 1.48-.393 1.48-1.316 0-.856-.562-1.243-1.48-1.243H2.4v2.559h1.093z"/>
    </svg>
  );
  if (match(["github.com", "github"])) return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
    </svg>
  );
  if (match(["vimeo.com", "vimeo"])) return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 12.13C4.603 9.542 3.83 8.247 3 8.247c-.194 0-.876.411-2.006 1.229L0 8.44c1.264-1.112 2.51-2.224 3.73-3.33C5.259 3.753 6.398 3.02 7.148 2.951c1.87-.18 3.016 1.1 3.447 3.84.465 2.953.789 4.789.971 5.51.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.487 4.801z"/>
    </svg>
  );
  if (match(["dribbble.com", "dribbble"])) return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5"/><path d="M8.56 2.75c4.37 6.03 6.02 9.42 8.03 17.72m2.54-15.38c-3.72 4.35-8.94 5.66-16.88 5.85m19.5 1.9c-3.5-.93-6.63-.82-8.94 0-2.58.92-5.01 2.86-7.44 6.32" fill="none" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );
  return <Globe className={className} strokeWidth={1.5} />;
}

export default function CandidatoDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { markCandidatoOpened } = useUnopenedCandidatos();
  const [candidato, setCandidato] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReescanando, setIsReescanando] = useState(false);
  const [isActualizandoCV, setIsActualizandoCV] = useState(false);
  const inputCVRef = useRef(null);
  const [openNombre, setOpenNombre] = useState(false);
  const [nombreValue, setNombreValue] = useState("");
  const [openNota, setOpenNota] = useState(false);
  const [openRango, setOpenRango] = useState(false);
  const [notaValue, setNotaValue] = useState("");
  const [rangoMinK, setRangoMinK] = useState("");
  const [rangoMaxK, setRangoMaxK] = useState("");
  const [openCarta, setOpenCarta] = useState(false);
  const [listas, setListas] = useState([]);
  const [nuevaListaNombre, setNuevaListaNombre] = useState("");
  const [isCreandoLista, setIsCreandoLista] = useState(false);
  const [openMensajeCandidato, setOpenMensajeCandidato] = useState(false);
  const [isDescartando, setIsDescartando] = useState(false);
  const [isRevisando, setIsRevisando] = useState(false);
  const [openListaDialog, setOpenListaDialog] = useState(false);
  const [openEditNotaIndex, setOpenEditNotaIndex] = useState(null);
  const [editNotaValue, setEditNotaValue] = useState("");
  const [carpetaEnLista, setCarpetaEnListaState] = useState(() => location.state?.candidatoCarpeta ?? null);
  const [revisadorEnLista, setRevisadorEnLista] = useState(() => location.state?.candidatoRevisador ?? null);
  const [listaRevisadorDetalles, setListaRevisadorDetalles] = useState({});
  const { showScore, getDisplayName, maskValue, demoHideCvPreview } = useSettings();
  const { user } = useAuth();

  const SITUACION_OPCIONES = [
    { value: "freelance", label: "Freelance" },
    { value: "asalariado", label: "Asalariado/a" },
    { value: "estudiante", label: "Estudiante" },
    { value: "ninguno", label: "En paro" },
  ];
  const candidatoIds = (location.state?.candidatoIds || []);
  const currentIndex = candidatoIds.indexOf(id);
  const prevId = currentIndex > 0 ? candidatoIds[currentIndex - 1] : null;
  const nextId = currentIndex >= 0 && currentIndex < candidatoIds.length - 1 ? candidatoIds[currentIndex + 1] : null;

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login");
      return;
    }
    
    loadCandidato();
    loadListas();
  }, [id, navigate]);

  useEffect(() => {
    if (id) markCandidatoOpened(id);
  }, [id, markCandidatoOpened]);

  useEffect(() => {
    const listaId = location.state?.fromListaId;
    if (!listaId || !id) return;
    getCandidatosDeLista(listaId)
      .then((data) => {
        const c = (data.candidatos || []).find((x) => x.id === id);
        if (c) {
          setCarpetaEnListaState(c.carpeta || null);
          setRevisadorEnLista(c.revisador || null);
          setListaRevisadorDetalles(data.lista?.revisador_detalles || {});
        }
      })
      .catch(() => {});
  }, [id, location.state?.fromListaId]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.target?.closest?.("input") || e.target?.closest?.("textarea") || e.target?.closest?.("[contenteditable]")) return;
      if (e.key === "ArrowLeft" && prevId) {
        e.preventDefault();
        navigate(`/candidatos/${prevId}`, { state: { ...location.state, candidatoIds } });
      } else if (e.key === "ArrowRight" && nextId) {
        e.preventDefault();
        navigate(`/candidatos/${nextId}`, { state: { ...location.state, candidatoIds } });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [prevId, nextId, candidatoIds, navigate, location.state]);

  const loadListas = async () => {
    try {
      const data = await getListas();
      setListas(data.listas || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleLista = async (lista) => {
    const estaEnLista = (lista.candidatos_ids || []).includes(id);
    const version = lista.version ?? undefined;
    try {
      if (estaEnLista) {
        await removeCandidatoFromLista(lista.id, id, version);
      } else {
        await addCandidatoToLista(lista.id, id, version);
      }
      await loadListas();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.detail || "Otro usuario ha modificado esta lista; recarga la página.");
        loadListas();
        return;
      }
      toast.error("Error al actualizar la lista");
    }
  };

  const handleCrearListaYAnadir = async () => {
    if (!nuevaListaNombre.trim()) return;
    setIsCreandoLista(true);
    try {
      const nueva = await createLista(nuevaListaNombre.trim());
      await addCandidatoToLista(nueva.id, id);
      setNuevaListaNombre("");
      await loadListas();
    } catch (err) {
      toast.error("Error al crear la lista");
    } finally {
      setIsCreandoLista(false);
    }
  };

  const handleRevisado = async () => {
    const listaId = location.state?.fromListaId;
    if (!listaId) return;
    const isCurrentlyRevisado = carpetaEnLista === "Revisados";
    setIsRevisando(true);
    try {
      await setCarpetaEnLista(listaId, id, isCurrentlyRevisado ? "Por revisar" : "Revisados");
      if (isCurrentlyRevisado) {
        setCarpetaEnListaState(null);
        setRevisadorEnLista(null);
        toast.success("Restaurado a Por revisar");
      } else {
        setCarpetaEnListaState("Revisados");
        setRevisadorEnLista(user?.email || null);
        toast.success("Candidato marcado como revisado");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al marcar como revisado");
    } finally {
      setIsRevisando(false);
    }
  };

  const handleDescartar = async () => {
    const listaId = location.state?.fromListaId;
    if (!listaId) return;
    const isCurrentlyDescartado = carpetaEnLista === "Descartados";
    setIsDescartando(true);
    try {
      await setCarpetaEnLista(listaId, id, isCurrentlyDescartado ? "Por revisar" : "Descartados");
      if (isCurrentlyDescartado) {
        setCarpetaEnListaState(null);
        setRevisadorEnLista(null);
        toast.success("Restaurado a Por revisar");
      } else {
        setCarpetaEnListaState("Descartados");
        setRevisadorEnLista(user?.email || null);
        toast.success("Candidato descartado en la lista");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al descartar");
    } finally {
      setIsDescartando(false);
    }
  };

  const loadCandidato = async () => {
    try {
      const data = await getCandidato(id);
      setCandidato(data);
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar el candidato");
      navigate("/candidatos");
    } finally {
      setIsLoading(false);
    }
  };

  const loadCandidatoRef = useRef(loadCandidato);
  loadCandidatoRef.current = loadCandidato;
  useEffect(() => {
    const handler = () => loadCandidatoRef.current();
    window.addEventListener(INTEREST_WEIGHTS_UPDATED_EVENT, handler);
    const onStorage = (e) => {
      if (e.key === INTEREST_WEIGHTS_STORAGE_KEY) loadCandidatoRef.current();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(INTEREST_WEIGHTS_UPDATED_EVENT, handler);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const handleToggleFavorito = async () => {
    try {
      const nuevo = !candidato.favorito;
      const payload = { favorito: nuevo };
      if (candidato.version != null) payload.version = candidato.version;
      await updateCandidato(id, payload);
      setCandidato((prev) => ({ ...prev, favorito: nuevo, version: (prev.version ?? 0) + 1 }));
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.detail || "Otro usuario ha modificado este candidato; recarga la página.");
        loadCandidato();
        return;
      }
      console.error(err);
      toast.error("Error al actualizar favorito");
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteCandidato(id);
      toast.success("Candidato eliminado");
      navigate("/candidatos");
    } catch (error) {
      console.error(error);
      toast.error("Error al eliminar el candidato");
    } finally {
      setIsDeleting(false);
    }
  };

  const openPdf = () => {
    const pdfUrl = getCandidatoPdfUrl(id);
    const token = getToken();
    // Open PDF in new tab with token
    window.open(`${pdfUrl}?token=${token}`, '_blank');
  };

  // Colores del score (igual que en CandidatosPage)
  const getScoreColors = (score) => {
    if (score >= 75) return "bg-emerald-100 text-emerald-700";
    if (score >= 50) return "bg-amber-100 text-amber-700";
    if (score >= 25) return "bg-orange-100 text-orange-700";
    return "bg-red-100 text-red-700";
  };

  // Parsea rango_salarial "15-20", "1.5-25", "1500-25000" o "25000" -> { minK, maxK } en miles (para comparación y display en "k")
  const parseRangoK = (str) => {
    if (!str || typeof str !== "string") return { minK: null, maxK: null };
    const cleaned = str.trim().replace(/k|€/gi, "");
    const parts = cleaned.split(/[-–—\s]+/).map((p) => p.trim().replace(/,/g, "."));
    const nums = parts.map((p) => parseFloat(p, 10)).filter((n) => !Number.isNaN(n) && n > 0);
    if (nums.length === 0) return { minK: null, maxK: null };
    const minRaw = Math.min(...nums);
    const maxRaw = Math.max(...nums);
    const toK = (n) => (n >= 1000 ? n / 1000 : n);
    return { minK: toK(minRaw), maxK: toK(maxRaw) };
  };

  // Ajuste de score por rango salarial: menos rango = más puntos (≤20k +5, 20-30k 0, >30k -5)
  const getSalaryScoreAdjustment = (rangoStr) => {
    const { maxK } = parseRangoK(rangoStr);
    if (maxK == null) return 0;
    if (maxK <= 20) return 5;
    if (maxK <= 30) return 0;
    return -5;
  };

  // Color del chip de rango: hasta 20k verde, 20k-30k naranja, +30k rojo (sutiles)
  const getRangoChipColors = (rangoStr) => {
    const { maxK } = parseRangoK(rangoStr);
    if (maxK == null) return "bg-muted text-muted-foreground";
    if (maxK <= 20) return "bg-emerald-100 text-emerald-700";
    if (maxK <= 30) return "bg-amber-100 text-amber-700";
    return "bg-red-100 text-red-700";
  };
  const getSituacionChipColors = (situacion) => {
    if (!situacion) return "bg-muted text-muted-foreground";
    switch (situacion) {
      case "freelance": return "bg-amber-100 text-amber-800";
      case "asalariado": return "bg-violet-100 text-violet-800";
      case "estudiante": return "bg-teal-100 text-teal-800";
      default: return "bg-slate-200 text-slate-700";
    }
  };
  const getSituacionLabel = (situacion) => {
    if (situacion === "freelance") return "Freelance";
    if (situacion === "asalariado") return "Asalariado/a";
    if (situacion === "estudiante") return "Estudiante";
    if (situacion === "ninguno") return "En paro";
    return "En paro"; // valor por defecto para admins cuando no está asignado
  };

  const formatRangoK = (k) => (k % 1 === 0 ? `${k}` : k.toFixed(1));
  const displayRangoLabel = (rangoStr) => {
    const { minK, maxK } = parseRangoK(rangoStr);
    if (minK == null && maxK == null) return rangoStr || "";
    if (minK != null && maxK != null && minK !== maxK) return `${formatRangoK(minK)}k - ${formatRangoK(maxK)}k`;
    if (maxK != null) return `${formatRangoK(maxK)}k`;
    return rangoStr || "";
  };

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!candidato) {
    return null;
  }

  const scoreConRango = (candidato.score_agencia || 0) + getSalaryScoreAdjustment(candidato.rango_salarial);
  const fecha = candidato.fecha_creacion 
    ? new Date(candidato.fecha_creacion).toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    : null;

  return (
    <div className="min-h-dvh bg-background">
      <AppMainHeader title="Candidatos" titleSuffix=" / Detalle" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/candidatos", location.state?.fromListaId ? { state: { openListaId: location.state.fromListaId, fromListaCarpetaTab: location.state.fromListaCarpetaTab } } : undefined)}
          className="text-sm"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          {location.state?.fromListaId ? "Volver a la lista" : "Candidatos"}
        </Button>
        {(prevId || nextId) && (
          <div className="md:hidden flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 rounded-full"
              disabled={!prevId}
              onClick={() => navigate(`/candidatos/${prevId}`, { state: { ...location.state, candidatoIds } })}
              aria-label="Candidato anterior"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={2} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 rounded-full"
              disabled={!nextId}
              onClick={() => navigate(`/candidatos/${nextId}`, { state: { ...location.state, candidatoIds } })}
              aria-label="Candidato siguiente"
            >
              <ArrowRight className="w-5 h-5" strokeWidth={2} />
            </Button>
          </div>
        )}
      </div>

      {/* Navegación entre candidatos: botones laterales */}
      {(prevId || nextId) && (
        <>
          {prevId && (
            <Button
              variant="outline"
              size="icon"
              className="hidden md:inline-flex fixed left-4 top-1/2 -translate-y-1/2 z-30 h-12 w-12 rounded-full shadow-md border-2 bg-background/95 hover:bg-muted"
              onClick={() => navigate(`/candidatos/${prevId}`, { state: { ...location.state, candidatoIds } })}
              aria-label="Candidato anterior"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={2} />
            </Button>
          )}
          {nextId && (
            <Button
              variant="outline"
              size="icon"
              className="hidden md:inline-flex fixed right-4 top-1/2 -translate-y-1/2 z-30 h-12 w-12 rounded-full shadow-md border-2 bg-background/95 hover:bg-muted"
              onClick={() => navigate(`/candidatos/${nextId}`, { state: { ...location.state, candidatoIds } })}
              aria-label="Candidato siguiente"
            >
              <ArrowRight className="w-5 h-5" strokeWidth={2} />
            </Button>
          )}
        </>
      )}

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Left Column - Info */}
          <div className="lg:col-span-2 space-y-8 animate-fade-in">
            {/* Header section */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-2xl font-semibold tracking-tight mb-1 break-words" data-testid="candidato-nombre">
                    {maskValue(candidato.nombre ?? "", "nombre") || "—"}
                  </h1>
                  <p className="text-muted-foreground">
                    {candidato.rol_principal || "Sin rol definido"}
                  </p>
                </div>
                {showScore && (
                  <span className={`inline-flex items-center justify-center text-2xl font-semibold px-3 py-1.5 rounded shrink-0 ${getScoreColors(scoreConRango)}`} data-testid="candidato-score">
                    {scoreConRango}
                  </span>
                )}
              </div>
              <div className="mb-6" />

              <div className="flex flex-wrap items-center gap-3">
                {candidato.nivel && (
                  <Badge variant="outline">{candidato.nivel}</Badge>
                )}
                {candidato.años_experiencia > 0 && (
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {candidato.años_experiencia} años de experiencia
                  </span>
                )}
              </div>
              <div className="space-y-2 mt-3">
                {/* Situación, contactado, rango + Like y menú Opciones */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-block text-sm font-medium px-2.5 py-1 rounded ${getSituacionChipColors(candidato.situacion_actual || "ninguno")}`}>
                      {getSituacionLabel(candidato.situacion_actual || "ninguno")}
                    </span>
                    {candidato.contactado && (
                      <span className="inline-block text-sm font-medium px-2.5 py-1 rounded bg-blue-100 text-blue-700">
                        Contactado
                      </span>
                    )}
                    {candidato.rango_salarial && (
                      <span className={`inline-block text-sm font-medium px-2.5 py-1 rounded ${getRangoChipColors(candidato.rango_salarial)}`}>
                        {displayRangoLabel(candidato.rango_salarial)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {location.state?.fromListaId && location.state?.fromListaTodosActivos && (() => {
                      const detRevisador = revisadorEnLista ? (listaRevisadorDetalles[revisadorEnLista] || (revisadorEnLista === user?.email ? { nombre: user?.nombre, color: user?.color, avatar_variant: user?.avatar_variant } : null)) : null;
                      const nombreRevisador = getDisplayName(revisadorEnLista) ?? ((detRevisador?.nombre || "").trim() || (revisadorEnLista || "").split("@")[0] || "?");
                      const isRevisado = carpetaEnLista === "Revisados";
                      const isDescartado = carpetaEnLista === "Descartados";
                      return (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={`text-xs h-8 px-2.5 gap-1.5 ${isRevisado ? "text-emerald-700 border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 hover:border-emerald-400" : "text-muted-foreground border-border bg-muted/50 hover:bg-muted hover:text-foreground"}`}
                            onClick={handleRevisado}
                            disabled={isRevisando}
                            title={isRevisado ? "Restaurar a Por revisar" : "Marcar como revisando en la lista"}
                          >
                            {isRevisando ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : isRevisado ? (
                              <>
                                <UserAvatar name={nombreRevisador} variant={detRevisador?.avatar_variant} color={detRevisador?.color} size={18} className="shrink-0" />
                                Revisado
                              </>
                            ) : (
                              <><Check className="w-3.5 h-3.5" strokeWidth={1.5} /> Marcar como revisando</>
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={`text-xs h-8 px-2.5 gap-1.5 ${isDescartado ? "text-orange-700 border-orange-300 hover:bg-orange-50 hover:text-orange-800 hover:border-orange-400" : "text-muted-foreground border-border bg-muted/50 hover:bg-muted hover:text-foreground"}`}
                            onClick={handleDescartar}
                            disabled={isDescartando}
                            title={isDescartado ? "Restaurar a Por revisar" : "Descartar en la lista"}
                          >
                            {isDescartando ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : isDescartado ? (
                              <>
                                <UserAvatar name={nombreRevisador} variant={detRevisador?.avatar_variant} color={detRevisador?.color} size={18} className="shrink-0" />
                                Descartado
                              </>
                            ) : (
                              <><XCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Descartar</>
                            )}
                          </Button>
                        </>
                      );
                    })()}
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="text-xs bg-muted/80 hover:bg-muted h-8 px-2.5"
                      onClick={handleToggleFavorito}
                      aria-label={candidato.favorito ? "Quitar de favoritos" : "Añadir a favoritos"}
                      title={candidato.favorito ? "Quitar de favoritos" : "Añadir a favoritos"}
                    >
                      <Heart
                        className={`w-3.5 h-3.5 ${candidato.favorito ? "fill-red-500 text-red-500" : "text-muted-foreground"}`}
                        strokeWidth={1.5}
                      />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="h-8 w-8 p-0 shrink-0 bg-muted/80 hover:bg-muted"
                          aria-label="Opciones"
                        >
                          <MoreVertical className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56" onCloseAutoFocus={(e) => e.preventDefault()}>
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); inputCVRef.current?.click(); }}>
                          <Upload className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                          Actualizar CV
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setNombreValue(candidato.nombre || ""); setOpenNombre(true); }}>
                          <User className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                          Actualizar nombre
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setOpenListaDialog(true); }}>
                          <ListChecks className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                          Añadir a lista
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setNotaValue(""); setOpenNota(true); }}>
                          <StickyNote className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                          Añadir nota
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={async (e) => {
                            e.preventDefault();
                            try {
                              const payload = { archivado: !candidato.archivado };
                              if (candidato.version != null) payload.version = candidato.version;
                              await updateCandidato(id, payload);
                              await loadCandidato();
                              toast.success(candidato.archivado ? "Candidato desarchivado" : "Candidato archivado");
                              if (!candidato.archivado) navigate("/candidatos");
                            } catch (err) {
                              if (err.response?.status === 409) {
                                toast.error(err.response?.data?.detail || "Otro usuario ha modificado este candidato; recarga la página.");
                                loadCandidato();
                                return;
                              }
                              toast.error("Error al actualizar");
                            }
                          }}
                        >
                          {candidato.archivado ? <ArchiveRestore className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> : <Archive className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />}
                          {candidato.archivado ? "Desarchivar" : "Archivar"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={async (e) => {
                            e.preventDefault();
                            try {
                              const payload = { contactado: !candidato.contactado };
                              if (candidato.version != null) payload.version = candidato.version;
                              await updateCandidato(id, payload);
                              await loadCandidato();
                              toast.success(candidato.contactado ? "Marcado como no contactado" : "Marcado como contactado");
                            } catch (err) {
                              if (err.response?.status === 409) {
                                toast.error(err.response?.data?.detail || "Otro usuario ha modificado este candidato; recarga la página.");
                                loadCandidato();
                                return;
                              }
                              toast.error("Error al actualizar");
                            }
                          }}
                        >
                          <PhoneCall className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                          Contactado
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setRangoMinK(parseRangoK(candidato.rango_salarial).minK != null ? String(parseRangoK(candidato.rango_salarial).minK) : ""); setRangoMaxK(parseRangoK(candidato.rango_salarial).maxK != null ? String(parseRangoK(candidato.rango_salarial).maxK) : ""); setOpenRango(true); }}>
                          <DollarSign className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                          Rango salarial
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={!candidato.pdf_data || isReescanando}
                          onSelect={async (e) => {
                            e.preventDefault();
                            setIsReescanando(true);
                            try {
                              await reescanearCandidato(id, candidato.version ?? undefined);
                              await loadCandidato();
                              toast.success("Currículum reescaneado correctamente");
                            } catch (err) {
                              if (err.response?.status === 409) {
                                toast.error(err.response?.data?.detail || "Otro usuario ha modificado este candidato; recarga la página.");
                                loadCandidato();
                                return;
                              }
                              toast.error(err.response?.data?.detail || "Error al reescanear");
                            } finally {
                              setIsReescanando(false);
                            }
                          }}
                        >
                          {isReescanando ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" strokeWidth={1.5} /> : <RefreshCw className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />}
                          Reescanear
                        </DropdownMenuItem>
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <User className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                            Situación actual
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            {SITUACION_OPCIONES.map((opt) => (
                              <DropdownMenuItem
                                key={opt.value}
                                onSelect={async (e) => {
                                  e.preventDefault();
                                  try {
                                    const payload = { situacion_actual: opt.value };
                                    if (candidato.version != null) payload.version = candidato.version;
                                    await updateCandidato(id, payload);
                                    await loadCandidato();
                                    toast.success(`Situación actualizada a ${opt.label}`);
                                  } catch (err) {
                                    if (err.response?.status === 409) {
                                      toast.error(err.response?.data?.detail || "Otro usuario ha modificado este candidato; recarga la página.");
                                      loadCandidato();
                                      return;
                                    }
                                    toast.error("Error al actualizar");
                                  }
                                }}
                              >
                                {opt.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive focus:text-destructive">
                              <Trash2 className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                              Eliminar
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar candidato?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción no se puede deshacer. Se eliminará permanentemente el perfil de {maskValue(candidato.nombre ?? "", "nombre") || "este candidato"}.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {isDeleting ? "Eliminando..." : "Eliminar"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                {/* Notas (debajo del bloque situación/botones) */}
                {(() => {
                  const notas = candidato.notas?.length
                    ? candidato.notas
                    : (candidato.nota != null && candidato.nota !== ""
                      ? [{ texto: candidato.nota, autor: candidato.nota_autor, fecha: null }]
                      : []);
                  if (notas.length === 0) return null;
                  const getAutorEmail = (n) => (typeof n.autor === "object" ? (n.autor?.email || "") : (n.autor || ""));
                  const isAuthor = (n) => (getAutorEmail(n) || "").trim().toLowerCase() === (user?.email || "").trim().toLowerCase();
                  return (
                    <div className="space-y-3">
                      {notas.map((n, i) => {
                        const autorUser = typeof n.autor === "object" ? n.autor : { email: n.autor, nombre: n.autor };
                        return (
                          <div key={i} className="group rounded-md bg-muted px-3 py-2 space-y-2 relative">
                            {n.autor != null && (
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-background/80 px-2 py-0.5 text-xs font-medium text-muted-foreground border border-border/50">
                                  <UserAvatar user={autorUser} size={18} className="shrink-0" />
                                  <span>{autorUser.nombre || autorUser.email}</span>
                                </span>
                              </div>
                            )}
                            <p className="text-sm text-muted-foreground italic pr-16">{n.texto}</p>
                            <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {isAuthor(n) && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                  onClick={() => { setEditNotaValue(n.texto || ""); setOpenEditNotaIndex(i); }}
                                  aria-label="Editar nota"
                                >
                                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                onClick={async () => {
                                  if (!confirm("¿Eliminar esta nota?")) return;
                                  try {
                                    const payload = { nota_indice_eliminar: i };
                                    if (candidato.version != null) payload.version = candidato.version;
                                    await updateCandidato(id, payload);
                                    await loadCandidato();
                                    toast.success("Nota eliminada");
                                  } catch (err) {
                                    if (err.response?.status === 409) {
                                      toast.error(err.response?.data?.detail || "Otro usuario ha modificado este candidato; recarga la página.");
                                      loadCandidato();
                                      return;
                                    }
                                    toast.error("Error al eliminar la nota");
                                  }
                                }}
                                aria-label="Eliminar nota"
                              >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>

            <Separator />

            {/* Trayectoria profesional (resumen del perfil) */}
            <div>
              <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                Trayectoria profesional
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed" data-testid="candidato-trayectoria">
                {candidato.resumen_perfil || "Sin información de trayectoria"}
              </p>
            </div>

            {/* Experiencia de mayor duración */}
            {candidato.experiencias_laborales && candidato.experiencias_laborales.length > 0 && (() => {
              const masLarga = candidato.experiencias_laborales.reduce((acc, e) => {
                const años = e && e.años_duracion != null ? Number(e.años_duracion) : 0;
                const accAños = acc && acc.años_duracion != null ? Number(acc.años_duracion) : 0;
                return años > accAños ? e : acc;
              }, null);
              return masLarga ? (
                <>
                  <Separator />
                  <div>
                    <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                      <Clock className="w-4 h-4" strokeWidth={1.5} />
                      Experiencia de mayor duración
                    </h2>
                    <div className="text-sm space-y-1" data-testid="candidato-experiencia-mas-larga">
                      <p className="font-medium">{masLarga.cargo || "Sin especificar"}</p>
                      {[masLarga.empresa, masLarga.periodo].filter(Boolean).length > 0 && (
                        <p className="text-muted-foreground text-xs">
                          {[masLarga.empresa, masLarga.periodo].filter(Boolean).join(" · ")}
                        </p>
                      )}
                      {masLarga.detalle && (
                        <p className="text-muted-foreground text-xs italic">{masLarga.detalle}</p>
                      )}
                    </div>
                  </div>
                </>
              ) : null;
            })()}

            <Separator />

            {/* Contacto y Enlaces aportados en 2 columnas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  Contacto y ubicación
                </h2>
                <div className="space-y-3">
                  {candidato.email && (() => {
                    const displayed = maskValue(candidato.email, "email");
                    const realTrimmed = String(candidato.email || "").trim();
                    const showLink = displayed === realTrimmed;
                    return (
                      <div className="flex items-center gap-3 text-sm" key="email">
                        <Mail className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                        {showLink ? (
                          <a href={`mailto:${candidato.email}`} className="hover:underline" data-testid="candidato-email">
                            {displayed}
                          </a>
                        ) : (
                          <span data-testid="candidato-email">{displayed}</span>
                        )}
                      </div>
                    );
                  })()}
                  {candidato.telefono && (() => {
                    const displayed = maskValue(candidato.telefono, "telefono");
                    const realTrimmed = String(candidato.telefono || "").trim();
                    const showLink = displayed === realTrimmed;
                    return (
                      <div className="flex items-center gap-3 text-sm" key="telefono">
                        <Phone className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                        {showLink ? (
                          <a href={`tel:${candidato.telefono}`} className="hover:underline" data-testid="candidato-telefono">
                            {displayed}
                          </a>
                        ) : (
                          <span data-testid="candidato-telefono">{displayed}</span>
                        )}
                      </div>
                    );
                  })()}
                  {(candidato.ciudad || candidato.provincia) && (
                    <div className="flex items-center gap-3 text-sm">
                      <MapPin className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      <span data-testid="candidato-ubicacion">
                        {maskValue([candidato.ciudad, candidato.provincia].filter(Boolean).join(", "), "direccion")}
                      </span>
                    </div>
                  )}
                  {(candidato.carnet_conducir || candidato.vehiculo_propio) && (
                    <div className="flex items-center gap-3 text-sm">
                      <Car className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      <span data-testid="candidato-vehiculo">
                        {[
                          candidato.carnet_conducir && "Carnet de conducir",
                          candidato.vehiculo_propio && "Vehículo propio"
                        ].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                  )}
                  {!candidato.email && !candidato.telefono && !candidato.ciudad && !candidato.provincia && (
                    <p className="text-sm text-muted-foreground">Sin información de contacto</p>
                  )}
                </div>
              </div>
              <div>
                <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                  <Link className="w-4 h-4" strokeWidth={1.5} />
                  Enlaces aportados
                </h2>
                {candidato.portfolios_manuales && candidato.portfolios_manuales.length > 0 ? (
                  <div className="space-y-2" data-testid="candidato-portfolios-manuales">
                    {candidato.portfolios_manuales.map((url, i) => {
                      const displayed = maskValue(url, "portfolio");
                      const realTrimmed = String(url || "").trim();
                      const showLink = displayed === realTrimmed;
                      return (
                        showLink ? (
                          <a
                            key={i}
                            href={url.startsWith('http') ? url : `https://${url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 text-sm hover:text-foreground transition-colors group"
                          >
                            <span className="text-muted-foreground group-hover:text-foreground shrink-0">
                              <SocialIcon url={url} className="w-4 h-4" />
                            </span>
                            <span className="text-muted-foreground group-hover:text-foreground truncate flex-1">
                              {displayed}
                            </span>
                            <ExternalLink className="w-3 h-3 text-muted-foreground/50 group-hover:text-foreground shrink-0" strokeWidth={1.5} />
                          </a>
                        ) : (
                          <div key={i} className="flex items-center gap-3 text-sm">
                            <span className="text-muted-foreground shrink-0">
                              <Link className="w-4 h-4" strokeWidth={1.5} />
                            </span>
                            <span className="text-muted-foreground truncate flex-1">{displayed}</span>
                          </div>
                        )
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Sin enlaces aportados</p>
                )}
              </div>
            </div>

            {/* Proyecto destacado */}
            {candidato.proyecto_destacado && (() => {
              const displayed = maskValue(candidato.proyecto_destacado, "portfolio");
              const realTrimmed = String(candidato.proyecto_destacado || "").trim();
              const showLink = displayed === realTrimmed;
              return (
                <>
                  <Separator />
                  <div>
                    <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                      <Link className="w-4 h-4" strokeWidth={1.5} />
                      Proyecto destacado
                    </h2>
                    {showLink ? (
                      <a
                        href={candidato.proyecto_destacado.startsWith("http") ? candidato.proyecto_destacado : `https://${candidato.proyecto_destacado}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-sm hover:text-foreground transition-colors group"
                        data-testid="candidato-proyecto-destacado"
                      >
                        <span className="text-muted-foreground group-hover:text-foreground shrink-0">
                          <SocialIcon url={candidato.proyecto_destacado} className="w-4 h-4" />
                        </span>
                        <span className="text-muted-foreground group-hover:text-foreground truncate flex-1">
                          {displayed}
                        </span>
                        <ExternalLink className="w-3 h-3 text-muted-foreground/50 group-hover:text-foreground shrink-0" strokeWidth={1.5} />
                      </a>
                    ) : (
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-muted-foreground shrink-0">
                          <Link className="w-4 h-4" strokeWidth={1.5} />
                        </span>
                        <span className="text-muted-foreground truncate flex-1">{displayed}</span>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}

            {/* Enlaces / Portfolio (extraídos del CV) */}
            {candidato.enlaces && candidato.enlaces.length > 0 && (
              <>
                <Separator />
                <div>
                  <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                    <Link className="w-4 h-4" strokeWidth={1.5} />
                    Portfolio y redes
                  </h2>
                  <div className="space-y-2" data-testid="candidato-enlaces">
                    {candidato.enlaces.map((enlace, i) => {
                      const displayed = maskValue(enlace.url, "portfolio");
                      const realTrimmed = String(enlace.url || "").trim();
                      const showLink = displayed === realTrimmed;
                      return showLink ? (
                        <a
                          key={i}
                          href={enlace.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 text-sm hover:text-foreground transition-colors group"
                        >
                          <span className="text-muted-foreground group-hover:text-foreground shrink-0">
                            <SocialIcon url={enlace.url} tipo={enlace.tipo} className="w-4 h-4" />
                          </span>
                          <span className="text-muted-foreground group-hover:text-foreground">
                            {enlace.tipo}
                          </span>
                          <span className="text-xs text-muted-foreground/70 truncate flex-1">
                            {displayed}
                          </span>
                          <ExternalLink className="w-3 h-3 text-muted-foreground/50 group-hover:text-foreground" strokeWidth={1.5} />
                        </a>
                      ) : (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <span className="text-muted-foreground shrink-0">
                            <Link className="w-4 h-4" strokeWidth={1.5} />
                          </span>
                          <span className="text-muted-foreground">{enlace.tipo}</span>
                          <span className="text-xs text-muted-foreground/70 truncate flex-1">{displayed}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Formación académica */}
            <div>
              <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                <GraduationCap className="w-4 h-4" strokeWidth={1.5} />
                Formación académica
              </h2>
              {candidato.estudios && candidato.estudios.length > 0 ? (
                <div className="space-y-3" data-testid="candidato-estudios">
                  {candidato.estudios.map((estudio, i) => (
                    <div key={i} className="text-sm">
                      <p className="font-medium">{estudio.titulo}</p>
                      <p className="text-muted-foreground text-xs">
                        {estudio.centro} {estudio.año && `· ${estudio.año}`}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sin estudios registrados</p>
              )}
            </div>

            <Separator />

            {/* Segmentos */}
            <div>
              <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                Segmentos
              </h2>
              {candidato.segmentos && candidato.segmentos.length > 0 ? (
                <div className="flex flex-wrap gap-2" data-testid="candidato-segmentos">
                  {candidato.segmentos.map((seg, i) => (
                    <Badge key={i} variant="secondary" className="text-sm font-normal">
                      {seg}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sin segmentos definidos</p>
              )}
            </div>

            <Separator />

            {/* Habilidades */}
            <div>
              <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                <Award className="w-4 h-4" strokeWidth={1.5} />
                Habilidades
              </h2>
              {candidato.habilidades && candidato.habilidades.length > 0 ? (
                <div className="flex flex-wrap gap-2" data-testid="candidato-habilidades">
                  {candidato.habilidades.map((hab, i) => (
                    <Badge key={i} variant="outline" className="text-sm font-normal">
                      {hab}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sin habilidades definidas</p>
              )}
            </div>

            <Separator />

            {/* Cursos */}
            {candidato.cursos && candidato.cursos.length > 0 && (
              <>
                <div>
                  <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                    Cursos y certificaciones
                  </h2>
                  <div className="flex flex-wrap gap-2" data-testid="candidato-cursos">
                    {candidato.cursos.map((curso, i) => (
                      <Badge key={i} variant="secondary" className="text-sm font-normal">
                        {curso}
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Premios */}
            {candidato.premios && candidato.premios.length > 0 && (
              <>
                <Separator />
                <div>
                  <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                    <Trophy className="w-4 h-4" strokeWidth={1.5} />
                    Premios y reconocimientos
                  </h2>
                  <div className="flex flex-wrap gap-2" data-testid="candidato-premios">
                    {candidato.premios.map((premio, i) => (
                      <Badge key={i} variant="outline" className="text-sm font-normal">
                        {premio}
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Idiomas */}
            <div>
              <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                <Languages className="w-4 h-4" strokeWidth={1.5} />
                Idiomas
              </h2>
              {candidato.idiomas && candidato.idiomas.length > 0 ? (
                <div className="flex flex-wrap gap-2" data-testid="candidato-idiomas">
                  {candidato.idiomas.map((idioma, i) => (
                    <Badge key={i} variant="outline" className="text-sm font-normal">
                      {idioma}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sin idiomas definidos</p>
              )}
            </div>

            <Separator />

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              {fecha && <span>Candidatura recibida el {fecha}</span>}
              {candidato?.id && (
                <span className="font-mono select-all opacity-50" title="ID del candidato">
                  {candidato.id}
                </span>
              )}
            </div>
          </div>

          {/* Right Column - PDF */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <div className="border rounded-lg overflow-hidden bg-muted/30">
                <div className="p-4 border-b bg-card flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                    <span className="text-sm font-medium">Currículum</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={openPdf}
                    className="text-xs h-10 sm:h-8"
                    data-testid="open-pdf-btn"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                    Abrir
                  </Button>
                </div>
                <div className="hidden md:block">
                {candidato.pdf_data ? (
                  demoHideCvPreview ? (
                    <div className="aspect-[3/4] bg-muted flex flex-col items-center justify-center rounded-lg border border-dashed border-muted-foreground/30 p-8 text-center">
                      <FileText className="w-16 h-16 text-muted-foreground/50 mb-4" strokeWidth={1} />
                      <p className="text-sm font-medium text-muted-foreground">Vista previa oculta</p>
                      <p className="text-xs text-muted-foreground mt-1">Modo presentación activo</p>
                    </div>
                  ) : (
                  <div className="aspect-[3/4] bg-muted relative pdf-preview-wrap">
                    <object
                      data={`${getCandidatoPdfUrl(id)}?token=${getToken()}#toolbar=0&navpanes=0`}
                      type="application/pdf"
                      className="w-full h-full min-h-[500px] block"
                      data-testid="pdf-object"
                    >
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                        <FileText className="w-12 h-12 text-muted-foreground/50 mb-3" strokeWidth={1} />
                        <p className="text-sm text-muted-foreground mb-4">
                          No se puede previsualizar el PDF en el navegador
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={openPdf}
                          className="text-xs"
                        >
                          <ExternalLink className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                          Abrir en nueva pestaña
                        </Button>
                      </div>
                    </object>
                  </div>
                  )
                ) : (
                  <div className="aspect-[3/4] bg-muted flex items-center justify-center">
                    <div className="text-center p-6">
                      <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" strokeWidth={1} />
                      <p className="text-sm text-muted-foreground">PDF no disponible</p>
                    </div>
                  </div>
                )}
                </div>
              </div>

              {/* Carta de presentación: abrir PDF en nueva pestaña o, si solo hay texto (legacy), en diálogo */}
              {(candidato.tiene_carta_pdf || (candidato.carta_presentacion && candidato.carta_presentacion.trim())) && (
                <div className="border border-input rounded-lg overflow-hidden mt-3">
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      <span className="text-sm font-medium">Carta de presentación</span>
                    </div>
                    {candidato.tiene_carta_pdf ? (
                      <a
                        href={`${getCandidatoCartaPdfUrl(id)}?token=${getToken()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                      >
                        <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Ver PDF
                      </a>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOpenCarta(true)}
                        className="text-xs"
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                        Abrir
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Mensaje del candidato (comentario del formulario) */}
              {candidato.comentario_candidato && candidato.comentario_candidato.trim() && (
                <Collapsible open={openMensajeCandidato} onOpenChange={setOpenMensajeCandidato} className="mt-3">
                  <div className="border border-input rounded-lg overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-2 p-3 text-left hover:bg-muted/50 transition-colors"
                      >
                        <span className="text-xs font-medium text-muted-foreground">Mensaje del candidato</span>
                        {openMensajeCandidato ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                        )}
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-3 pb-3 pt-0">
                        <p className="text-xs text-muted-foreground whitespace-pre-wrap italic">{candidato.comentario_candidato}</p>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}

              <input
                ref={inputCVRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                aria-hidden
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  if (!file.name.toLowerCase().endsWith(".pdf")) {
                    toast.error("Solo se permiten archivos PDF");
                    return;
                  }
                  if (file.size > 5 * 1024 * 1024) {
                    toast.error("El archivo no puede superar 5 MB");
                    return;
                  }
                  setIsActualizandoCV(true);
                  try {
                    await actualizarCandidatoCV(id, file, candidato?.version ?? undefined);
                    await loadCandidato();
                    toast.success("CV actualizado y perfil reescaneado correctamente");
                  } catch (err) {
                    if (err.response?.status === 409) {
                      toast.error(err.response?.data?.detail || "Otro usuario ha modificado este candidato; recarga la página.");
                      loadCandidato();
                      return;
                    }
                    console.error(err);
                    toast.error(err.response?.data?.detail || "Error al actualizar el CV");
                  } finally {
                    setIsActualizandoCV(false);
                  }
                }}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Diálogo Actualizar nombre */}
      <Dialog open={openNombre} onOpenChange={setOpenNombre}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Actualizar nombre</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="nombre">Nombre del candidato</Label>
            <Input
              id="nombre"
              value={nombreValue}
              onChange={(e) => setNombreValue(e.target.value)}
              placeholder="Nombre y apellidos"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNombre(false)}>Cancelar</Button>
            <Button
              onClick={async () => {
                const value = nombreValue.trim();
                if (!value) {
                  toast.error("El nombre no puede estar vacío");
                  return;
                }
                try {
                  const payload = { nombre: value };
                  if (candidato?.version != null) payload.version = candidato.version;
                  await updateCandidato(id, payload);
                  await loadCandidato();
                  setOpenNombre(false);
                  toast.success("Nombre actualizado");
                } catch (err) {
                  if (err.response?.status === 409) {
                    toast.error(err.response?.data?.detail || "Otro usuario ha modificado este candidato; recarga la página.");
                    loadCandidato();
                    return;
                  }
                  console.error(err);
                  toast.error(err.response?.data?.detail || "Error al actualizar el nombre");
                }
              }}
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo Añadir nota */}
      <Dialog open={openNota} onOpenChange={setOpenNota}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Añadir nota</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="nota">Nota aclaratoria sobre el candidato</Label>
            <Textarea
              id="nota"
              value={notaValue}
              onChange={(e) => setNotaValue(e.target.value)}
              placeholder="Ej: Interesado en remoto, disponible desde junio..."
              rows={4}
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNota(false)}>Cancelar</Button>
            <Button
              onClick={async () => {
                if (!notaValue.trim()) return;
                try {
                  const payload = { nota: notaValue.trim() };
                  if (candidato?.version != null) payload.version = candidato.version;
                  await updateCandidato(id, payload);
                  await loadCandidato();
                  setNotaValue("");
                  setOpenNota(false);
                  toast.success("Nota añadida");
                } catch (err) {
                  if (err.response?.status === 409) {
                    toast.error(err.response?.data?.detail || "Otro usuario ha modificado este candidato; recarga la página.");
                    loadCandidato();
                    return;
                  }
                  console.error(err);
                  toast.error("Error al guardar la nota");
                }
              }}
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo Editar nota */}
      <Dialog open={openEditNotaIndex !== null} onOpenChange={(open) => { if (!open) setOpenEditNotaIndex(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar nota</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="edit-nota">Texto de la nota</Label>
            <Textarea
              id="edit-nota"
              value={editNotaValue}
              onChange={(e) => setEditNotaValue(e.target.value)}
              placeholder="Texto de la nota..."
              rows={4}
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEditNotaIndex(null)}>Cancelar</Button>
            <Button
              onClick={async () => {
                const text = editNotaValue.trim();
                if (text === "" || openEditNotaIndex === null) return;
                try {
                  const payload = { nota_indice_editar: openEditNotaIndex, nota_texto_editar: text };
                  if (candidato?.version != null) payload.version = candidato.version;
                  await updateCandidato(id, payload);
                  await loadCandidato();
                  setOpenEditNotaIndex(null);
                  setEditNotaValue("");
                  toast.success("Nota actualizada");
                } catch (err) {
                  if (err.response?.status === 403) {
                    toast.error("Solo el autor puede editar esta nota");
                    setOpenEditNotaIndex(null);
                    return;
                  }
                  if (err.response?.status === 409) {
                    toast.error(err.response?.data?.detail || "Otro usuario ha modificado este candidato; recarga la página.");
                    loadCandidato();
                    return;
                  }
                  toast.error("Error al guardar la nota");
                }
              }}
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo Rango salarial: min y max en miles (solo número, la k se añade) */}
      <Dialog open={openRango} onOpenChange={setOpenRango}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rango salarial</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rango-min">Mínimo (k€)</Label>
              <div className="flex items-center gap-1">
                <Input
                  id="rango-min"
                  type="text"
                  inputMode="decimal"
                  placeholder="15 o 1.5"
                  value={rangoMinK}
                  onChange={(e) => setRangoMinK(e.target.value.replace(/[^\d.]/g, "").replace(/^(\d*\.?\d*).*/, "$1"))}
                />
                <span className="text-sm text-muted-foreground shrink-0">k</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rango-max">Máximo (k€)</Label>
              <div className="flex items-center gap-1">
                <Input
                  id="rango-max"
                  type="text"
                  inputMode="decimal"
                  placeholder="20 o 25"
                  value={rangoMaxK}
                  onChange={(e) => setRangoMaxK(e.target.value.replace(/[^\d.]/g, "").replace(/^(\d*\.?\d*).*/, "$1"))}
                />
                <span className="text-sm text-muted-foreground shrink-0">k</span>
              </div>
            </div>
          </div>
          {(candidato?.rango_salarial || rangoMinK || rangoMaxK) && (
            <p className="text-xs text-muted-foreground">
              <button
                type="button"
                className="underline hover:no-underline"
                onClick={async () => {
                  try {
                    const payload = { rango_salarial: "" };
                    if (candidato?.version != null) payload.version = candidato.version;
                    await updateCandidato(id, payload);
                    await loadCandidato();
                    setRangoMinK("");
                    setRangoMaxK("");
                    setOpenRango(false);
                    toast.success("Rango salarial quitado");
                  } catch (err) {
                    if (err.response?.status === 409) {
                      toast.error(err.response?.data?.detail || "Otro usuario ha modificado este candidato; recarga la página.");
                      loadCandidato();
                      return;
                    }
                    console.error(err);
                    toast.error("Error al quitar el rango salarial");
                  }
                }}
              >
                Quitar rango salarial
              </button>
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenRango(false)}>Cancelar</Button>
            <Button
              onClick={async () => {
                const min = rangoMinK.trim();
                const max = rangoMaxK.trim();
                const finalValue = min && max ? `${min}-${max}` : min ? min : max ? max : "";
                try {
                  const payload = { rango_salarial: finalValue };
                  if (candidato?.version != null) payload.version = candidato.version;
                  await updateCandidato(id, payload);
                  await loadCandidato();
                  setOpenRango(false);
                  toast.success(finalValue ? "Rango salarial guardado" : "Rango salarial quitado");
                } catch (err) {
                  if (err.response?.status === 409) {
                    toast.error(err.response?.data?.detail || "Otro usuario ha modificado este candidato; recarga la página.");
                    loadCandidato();
                    return;
                  }
                  console.error(err);
                  toast.error("Error al guardar el rango salarial");
                }
              }}
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openCarta} onOpenChange={setOpenCarta}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Carta de presentación</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 rounded-md border bg-muted/30 p-4">
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{candidato?.carta_presentacion || ""}</p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={openListaDialog} onOpenChange={setOpenListaDialog}>
        <DialogContent className="w-full max-w-sm p-0">
          <div className="p-3 border-b">
            <DialogHeader>
              <DialogTitle className="text-sm">Añadir a lista</DialogTitle>
            </DialogHeader>
          </div>
          <div className="p-3 border-b max-h-48 overflow-y-auto">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Listas</p>
            {listas.length === 0 ? (
              <p className="text-xs text-muted-foreground">No hay listas creadas aún</p>
            ) : (
              <div className="space-y-0.5">
                {listas.map((lista) => {
                  const estaEnLista = (lista.candidatos_ids || []).includes(id);
                  return (
                    <button
                      key={lista.id}
                      type="button"
                      onClick={() => handleToggleLista(lista)}
                      className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors text-left"
                    >
                      <span className="truncate">{lista.nombre}</span>
                      {estaEnLista && <Check className="w-3.5 h-3.5 text-foreground shrink-0" strokeWidth={2} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="p-3">
            <p className="text-xs text-muted-foreground mb-2">Nueva lista</p>
            <div className="flex gap-1.5">
              <Input
                placeholder="Nombre..."
                value={nuevaListaNombre}
                onChange={(e) => setNuevaListaNombre(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleCrearListaYAnadir(); }}
                className="h-7 text-xs"
              />
              <Button
                size="sm"
                variant="secondary"
                className="h-7 w-7 p-0 shrink-0"
                disabled={!nuevaListaNombre.trim() || isCreandoLista}
                onClick={handleCrearListaYAnadir}
              >
                {isCreandoLista ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" strokeWidth={2} />}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
